import * as Sentry from '@sentry/react';
import Cookies from 'js-cookie';
import { Loader } from '@mantine/core';
import classNames from 'classnames';
import React, { useCallback, useContext, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import setCouponCookie from '../../lib/setCouponCookie';
import setReferralCookie from '../../lib/setReferralCookie';
import { COUPON_COOKIE_NAME, expectUnreachable, getLocalizedPricing } from '../../lib/util';
import { useCartPricing, useCartPricingRefreshing } from '../../contexts/CartPricingContext';
import usePromoCodeEligibility from '../../hooks/usePromoCodeEligibility';
import * as events from '../../lib/analytics/events';
import CheckoutContext from '../../lib/CheckoutContext';
import { CouponError, GiftCardError, ReferralCodeError, logInternalError, PromoCodeError } from '../../lib/errors';
import { validatePromoCode, PromoCodeValidationErrorResponse } from '../../lib/promoCode';
import { REFERRAL_COOKIE_NAME } from '../../lib/util/referralCookie';
import * as types from '../../types';
import { ReferralSource } from '../../types/gql-op-types';
import ErrorMessage from '../ErrorMessage';
import styles from './PromoCode.module.scss';
import PromoCodeForm from './PromoCodeForm';
import { isAxiosError } from '../../lib/fi-api/apiUtils';
import { PREPAID_COUPON_PREFIXES_TO_REPLACE } from '../../lib/giftWithPurchasePromo';
import { getFiGrowthBook } from '../../lib/growthbook';
import { ReactComponent as CloseIcon } from '../../assets/images/icons/close.svg';
import {
  getCartTestTotalDiscount,
  getCartV2CurrencyFormattedTotal,
  getCartV2FormattedTotal,
} from '../CartSummary/CartActions';
import useProductSkuMap from '../../hooks/useProductSkuMap';
import { ReactComponent as CheckGreenIcon } from '../../assets/images/icons/check_green.svg';
import { appliedCouponDiscountInCents, isBundleCartItem } from '../../lib/cart';
import { calculatePositionSavings } from '../../lib/positionDiscount';
import { useLocale } from '../../contexts/LocaleContext';
import { showRescueGwaVariant } from '../../views/RescueLanding/RescueBanner';

function DiscountAppliedItem({
  discountMessage,
  removeDiscount,
  pending = false,
  variant = 'default',
}: {
  discountMessage: string;
  removeDiscount?: () => void;
  /** the discount is being recalculated, so the message is dimmed */
  pending?: boolean;
  /**
   * `confirmation` is the rescue cart's proof that the auto-applied adoption discount landed. It is
   * a bordered box rather than the pill the other variants use: under auto-apply this row is the
   * only signal the adopter gets, so it is sized to be read rather than glanced past.
   */
  variant?: 'default' | 'gray' | 'confirmation';
}) {
  const className =
    variant === 'gray'
      ? styles.discountAppliedItemGray
      : variant === 'confirmation'
        ? styles.discountAppliedItemConfirmation
        : styles.discountAppliedItem;
  return (
    <div className={className}>
      {variant === 'confirmation' && <CheckGreenIcon className={styles.discountAppliedItemCheck} />}
      {pending && <Loader size={12} color="dark" aria-hidden />}
      <div className={classNames(styles.discountAppliedItemMessage, { [styles.pending]: pending })} aria-busy={pending}>
        {discountMessage}
      </div>
      {removeDiscount && (
        <span onClick={removeDiscount}>
          <CloseIcon />
        </span>
      )}
    </div>
  );
}

function couponDiscountMessage(
  cartPricing: types.CartPricing,
  cart: types.Cart,
  freeTrialValueInCents: number,
  t: TFunction,
  currencyFormatter: Intl.NumberFormat,
  wholeNumberCurrencyFormatter: Intl.NumberFormat,
) {
  const couponDiscount = cartPricing.couponDiscount;
  if (couponDiscount?.type === 'free_trial') {
    const freeTrialMonths = couponDiscount.trialLength;
    return t('coupon_code_months_free_value', {
      couponCode: cart.couponCode,
      months: freeTrialMonths,
      value: getCartV2CurrencyFormattedTotal(freeTrialValueInCents, currencyFormatter, wholeNumberCurrencyFormatter),
    });
  }

  const cartTestTotalDiscount = getCartTestTotalDiscount(cartPricing, 'promo_only');
  const discountAmount = getCartV2CurrencyFormattedTotal(
    cartTestTotalDiscount,
    currencyFormatter,
    wholeNumberCurrencyFormatter,
  );

  // One off adjustment for displaying coupon code.
  // We don't want to share this elevated offer publicly.
  if (cart.couponCode === 'nano99') {
    return t('dollar_amount_off', { amount: discountAmount });
  } else {
    return t('coupon_code_dollar_amount_off', {
      couponCode: cart.couponCode,
      amount: discountAmount,
    });
  }
}

/**
 * every branch reads the discount back off the pricing the server returned for this cart, because the
 * point of the message is to prove the adoption discount landed. a coupon we cannot quantify falls
 * through to the bare confirmation rather than naming an amount we did not measure.
 */
function adoptionPromoMessage(
  cartPricing: types.CartPricing,
  t: TFunction,
  currencyFormatter: Intl.NumberFormat,
  wholeNumberCurrencyFormatter: Intl.NumberFormat,
) {
  const couponDiscount = cartPricing.couponDiscount;
  if (couponDiscount?.type === 'free_trial' && couponDiscount.trialUnit === 'month') {
    return t('adoption_promo_applied_months_free', { count: couponDiscount.trialLength });
  }

  const discountInCents = appliedCouponDiscountInCents(cartPricing);
  if (discountInCents > 0) {
    return t('adoption_promo_applied_amount_off', {
      amount: getCartV2CurrencyFormattedTotal(discountInCents, currencyFormatter, wholeNumberCurrencyFormatter),
    });
  }

  return t('adoption_promo_applied');
}

interface PromoCodeProps {
  /** Field placeholder when the omnibox is open to every code type; the rescue variant keeps its own. */
  placeholder?: string;
}

export default function PromoCode({ placeholder }: PromoCodeProps = {}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const cartPricing = useCartPricing();
  const cartPricingRefreshing = useCartPricingRefreshing();
  const { cart, checkoutActions } = useContext(CheckoutContext);
  const [error, setError] = useState<string | null>(null);
  const showCoupons = useSelector((state: types.AppState) => !!state.config.siteConfig.showCoupons);
  const [pendingCodeValue, setPendingCodeValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const showEcomCartTest = getFiGrowthBook().getFeatureValue('ecom-cart-test', false);
  const siteConfig = useSelector((state: types.AppState) => state.config.siteConfig);
  const freeTrialValueInCents = siteConfig.gwpFreeTrialDisplayValueInCents;
  const productSkuMap = useProductSkuMap();
  const { currency, currencyFormatter, wholeNumberCurrencyFormatter } = useLocale();

  // Calculate position discount savings for the multi-pet offer pill
  const positionDiscountSavings = useMemo(() => {
    const { positionDiscounts } = cartPricing;
    if (!positionDiscounts) return 0;

    return Object.entries(positionDiscounts).reduce((total, [cartItemId, discount]) => {
      if (!discount.isEnrolled || discount.discountPercent === 0) return total;

      const cartItem = cart.cartItems[cartItemId];
      if (!cartItem || !isBundleCartItem(cartItem)) return total;

      const subscriptionProduct = productSkuMap.get(cartItem.subscriptionLineItem.sku)?.product;
      if (!subscriptionProduct || !types.isModuleSubscriptionProduct(subscriptionProduct)) return total;

      const subscriptionPrice = getLocalizedPricing(subscriptionProduct.pricingInCents, currency);
      return total + calculatePositionSavings(subscriptionPrice, discount.discountPercent);
    }, 0);
  }, [cartPricing, cart.cartItems, productSkuMap, currency]);

  const onResolved = useCallback((couponCodeOverride?: string) => {
    setSubmitting(false);
    setPendingCodeValue(couponCodeOverride ?? '');
  }, []);

  const { couponCodeEligibility, giftCardEligibility, referralCodeEligibility, adoptionPromoApplied } =
    usePromoCodeEligibility({ showCoupons });

  // When promo codes are applied from url redirects, they can get attached to carts even if they're not valid for the
  // current cart. This is because we don't want to lose the promo code if the user adds an item to the cart
  // that makes it valid. When applying a new code, we want to ignore any invalid codes that are attached just in case
  // the backend would reject the new code due to stacking restrictions.
  const cartExcludingInvalidCodes: types.Cart = useMemo(
    () => ({
      ...cart,
      couponCode: cartPricing.validationErrors?.couponCode ? undefined : cart.couponCode,
      referralCode: cartPricing.validationErrors?.referralCode ? undefined : cart.referralCode,
      redeemedGiftCardCode: cartPricing.validationErrors?.redeemedGiftCardCode ? undefined : cart.redeemedGiftCardCode,
    }),
    [
      cart,
      cartPricing.validationErrors?.couponCode,
      cartPricing.validationErrors?.redeemedGiftCardCode,
      cartPricing.validationErrors?.referralCode,
    ],
  );

  /**
   * Even though we may have already determined eligibility for our code types via usePromoCodeEligibility, we still do
   * the checks for each when submitting the form because we use an onmibox and we want to differentiate error messages
   * between a code that is not found and a code that is found but cannot be applied due to eligibility reasons.
   */
  const onSubmit = useCallback(async () => {
    setSubmitting(true);
    // Reset error state
    setError(null);
    let overrideCouponCode: string | undefined;

    try {
      const result = await validatePromoCode(cartExcludingInvalidCodes, pendingCodeValue);
      if (result.promoCodeType === 'coupon') {
        if (!couponCodeEligibility.canApplyCode) {
          throw CouponError.ineligible(couponCodeEligibility.ineligibilityReason);
        }

        dispatch(checkoutActions.addCoupon(pendingCodeValue));
        events.cartPage.couponApplied(pendingCodeValue);
        setCouponCookie(pendingCodeValue);
      } else if (result.promoCodeType === 'referral') {
        if (!referralCodeEligibility.canApplyCode) {
          throw ReferralCodeError.ineligible(referralCodeEligibility.ineligibilityReason);
        }

        dispatch(
          checkoutActions.addReferral({
            code: pendingCodeValue,
            referrerName: result.referrerName,
            referrerAvatarUrl: result.referrerAvatarUrl,
            referrerPetAvatarUrl: result.referrerPetAvatarUrl,
            source: ReferralSource.DirectLink,
          }),
        );
        events.cartPage.referralCodeApplied(pendingCodeValue);
        setReferralCookie(pendingCodeValue, result.referrerName, ReferralSource.DirectLink);
      } else if (result.promoCodeType === 'giftCard') {
        if (!giftCardEligibility.canApplyCode) {
          throw GiftCardError.ineligible(giftCardEligibility.ineligibilityReason);
        }

        dispatch(checkoutActions.addGiftCard(pendingCodeValue));
        events.cartPage.giftCardApplied(pendingCodeValue);
      } else if (result.promoCodeType === 'internalCoupon') {
        if (!couponCodeEligibility.canApplyCode) {
          throw CouponError.ineligible(couponCodeEligibility.ineligibilityReason);
        }

        dispatch(checkoutActions.addCoupon(pendingCodeValue));
        events.cartPage.couponApplied(pendingCodeValue);
        setCouponCookie(pendingCodeValue);
      } else {
        expectUnreachable(result.promoCodeType);
        throw new Error(t('unexpected_promo_code_type'));
      }
    } catch (err) {
      if (isAxiosError(err) && err.response?.data.error) {
        const errorResponseData: PromoCodeValidationErrorResponse = err.response?.data.error;
        const matchingOverridePrefix = PREPAID_COUPON_PREFIXES_TO_REPLACE.map((p) => p.toLowerCase()).find(
          (prefix) =>
            pendingCodeValue.toLowerCase().startsWith(prefix) &&
            !pendingCodeValue.toLowerCase().startsWith(`${prefix}a-`),
        );
        // We retry the alternate code for these errors because the alternate code was generated
        // when we needed to change the subscription plans in scope of the coupon (after adding new
        // plans to our catalog) OR as a way to extend coupons that have already expired.
        const shouldRetryWithAlternateCode =
          (errorResponseData.code === 'coupon_no_eligible_items' || errorResponseData.code === 'expired_coupon') &&
          matchingOverridePrefix;
        if (shouldRetryWithAlternateCode) {
          const codeToUse = pendingCodeValue
            .toLowerCase()
            .replace(matchingOverridePrefix.toLowerCase(), `${matchingOverridePrefix.toLowerCase()}a-`);

          setError(t('almost_done_click_apply_again_to_use_your_code'));

          // Set the override code so that the form is updated with the adjusted code
          overrideCouponCode = codeToUse;
        } else {
          setError(errorResponseData.message);

          // This is a user-facing error but we'll log it to Sentry as well. We like to know about spikes
          // even in coupon errors that are just validation errors (e.g. that coupon doesn't apply to XYZ) as it could
          // indicate a misconfiguration of the coupon. Sentry allows for a more intuitive interface than OpenSearch
          // anomaly detection alerts.
          Sentry.captureMessage(errorResponseData.message, {
            level: 'info',
            tags: {
              messageType: 'promoCodeValidationError',
            },
          });
        }
      } else if (err instanceof PromoCodeError) {
        setError(err.message);

        // This is a user-facing error but we'll log it to Sentry as well. We like to know about spikes
        // even in coupon errors that are just validation errors (e.g. that coupon doesn't apply to XYZ) as it could
        // indicate a misconfiguration of the coupon. Sentry allows for a more intuitive interface than OpenSearch
        // anomaly detection alerts.
        Sentry.captureMessage(err.message, {
          level: 'info',
          tags: {
            messageType: 'promoCodeValidationError',
          },
        });
      } else {
        setError(t('an_unknown_error_occurred_please_try_again'));
        logInternalError(err, {
          // For unknown coupon errors, separate errors by the message
          fingerprint: ['{{ default }}', err.message],
          tags: {
            checkoutStep: 'promoCode',
          },
        });
      }

      Cookies.remove(COUPON_COOKIE_NAME);
      Cookies.remove(REFERRAL_COOKIE_NAME);
    } finally {
      onResolved(overrideCouponCode);
    }
  }, [
    cartExcludingInvalidCodes,
    checkoutActions,
    couponCodeEligibility,
    dispatch,
    giftCardEligibility,
    onResolved,
    pendingCodeValue,
    referralCodeEligibility,
    t,
  ]);

  // If they cannot apply any codes, don't show the form
  if (
    !couponCodeEligibility.canApplyCode &&
    !giftCardEligibility.canApplyCode &&
    !referralCodeEligibility.canApplyCode
  ) {
    return null;
  }

  const disabled = submitting || pendingCodeValue === '';
  // eligibility gate, not the redesign one: the placeholder drops the coupon half of the omnibox for
  // every adopter, since a rescue arrival has no coupon code of their own to type. `adoptionPromoApplied`
  // is the stricter one - while the gift is on the cart the field refuses codes, so the discount is
  // rendered as a non-removable confirmation rather than a chip a shopper could delete with no way back.
  const rescueGwaVariant = showRescueGwaVariant();

  return (
    <div className={styles.main}>
      <PromoCodeForm
        actionLabel={t('apply')}
        disabled={disabled}
        onChange={(code: string) => {
          setPendingCodeValue(code);
          setError(null);
        }}
        onSubmit={onSubmit}
        pendingCodeValue={pendingCodeValue}
        placeholder={rescueGwaVariant ? t('have_a_gift_card_or_referral_code') : (placeholder ?? t('have_a_code'))}
      />
      {adoptionPromoApplied && (
        <DiscountAppliedItem
          variant="confirmation"
          discountMessage={adoptionPromoMessage(cartPricing, t, currencyFormatter, wholeNumberCurrencyFormatter)}
        />
      )}
      {!!cart.couponCode && showEcomCartTest && !cart.referralCode && !rescueGwaVariant && (
        <DiscountAppliedItem
          // until the new pricing lands, the amount belongs to the cart before the code, so show only the code
          discountMessage={
            cartPricingRefreshing
              ? cart.couponCode
              : couponDiscountMessage(
                  cartPricing,
                  cart,
                  freeTrialValueInCents,
                  t,
                  currencyFormatter,
                  wholeNumberCurrencyFormatter,
                )
          }
          pending={cartPricingRefreshing}
          removeDiscount={() => {
            if (!cart.couponCode) return;
            dispatch(checkoutActions.removeCoupon(cart.couponCode));
            events.cartPage.couponRemoved(cart.couponCode);
          }}
        />
      )}
      {!!cart.referralCode && showEcomCartTest && (
        <DiscountAppliedItem
          discountMessage={t('referral_code_applied_with_code', { referralCode: cart.referralCode })}
          removeDiscount={() => {
            dispatch(checkoutActions.removeReferral({ code: cart.referralCode }));
          }}
        />
      )}
      {!!cart.redeemedGiftCardCode && showEcomCartTest && (
        <DiscountAppliedItem
          discountMessage={t('gift_card_colon_code', { code: cart.redeemedGiftCardCode })}
          removeDiscount={() => {
            dispatch(checkoutActions.removeGiftCard({}));
          }}
        />
      )}
      {!!cartPricing.appliedAccountBalanceInCents && showEcomCartTest && (
        <DiscountAppliedItem discountMessage={t('account_balance_applied_discount_item')} />
      )}
      {positionDiscountSavings > 0 && showEcomCartTest && (
        <DiscountAppliedItem
          discountMessage={t('multi_pet_offer_amount_off', {
            amount: getCartV2FormattedTotal(positionDiscountSavings),
          })}
          variant="gray"
        />
      )}
      {error && <ErrorMessage errors={[error]} />}
    </div>
  );
}
